import * as React from 'react';

import { AlertCircle, PackageSearch } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Template } from '@klareospace/pharos-lib-types';
import { Button, GridSkeleton } from '@klareospace/pharos-lib-web-utils';

import { CatalogCard } from './catalog-card';

// ─── Props ────────────────────────────────────────────────────────────────────

interface CatalogGridProps {
  emptyMessage: string;
  error: Error | null;
  errorMessage: string;
  installLabel: string;
  installedLabel: string;
  isInstalled?: (templateId: string) => boolean;
  isLoading: boolean;
  onInstall: (template: Template) => void;
  onRetry: () => void;
  templates: Template[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CatalogGrid({
  emptyMessage,
  error,
  errorMessage,
  installLabel,
  installedLabel,
  isInstalled,
  isLoading,
  onInstall,
  onRetry,
  templates,
}: CatalogGridProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('settings');

  if (isLoading) {
    return <GridSkeleton columns={2} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="text-sm text-muted-foreground">{t(errorMessage)}</p>
        <Button onClick={onRetry} size="sm" variant="outline">
          {tCommon('errors.retry')}
        </Button>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <PackageSearch className="h-10 w-10 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{t(emptyMessage)}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      {templates.map((template) => (
        <CatalogCard
          installLabel={installLabel}
          installedLabel={installedLabel}
          isInstalled={isInstalled?.(template.id) ?? false}
          key={template.id}
          onInstall={onInstall}
          template={template}
        />
      ))}
    </div>
  );
}
